import type {
	FormEventGroup,
	WaitlistFigures,
} from 'pkg/api/models/form_event_group';
import { PricingType } from 'pkg/api/models/form_event_group';

export function isPricedPerEvent(eventGroup: FormEventGroup): boolean {
	return (
		!!eventGroup.productPriceId &&
		eventGroup.pricingType === PricingType.PerEvent
	);
}

/**
 * Whether a remaining/total pair reads as "nearly full": a smaller total reads as nearly full
 * sooner (30% of total at 10 or under) than a larger one (10% above that). Zero remaining is
 * "full", not "nearly full" -- callers that show a different badge at zero (e.g. "No spots
 * left") exclude it themselves. Shared by AvailableSpots (components/forms/available-spots,
 * form-level) and the event group picker (submission/form/event_groups, per-booking).
 */
export function isNearlyFull(remaining: number, total: number): boolean {
	const threshold = total <= 10 ? 0.3 : 0.1;

	return remaining !== 0 && remaining / total < threshold;
}

/** Whether a parsed capacity is uncapped (null) or a whole number of at least 1. */
export function isValidCapacity(capacity: number | null): boolean {
	return capacity === null || (Number.isInteger(capacity) && capacity >= 1);
}

export function raisesWaitlistCapacity(
	eventGroup: FormEventGroup | undefined,
	capacity: number | null,
	waitlistEnabled: boolean
): boolean {
	return (
		!!eventGroup?.waitlistEnabled &&
		waitlistEnabled &&
		eventGroup.capacity !== null &&
		capacity !== null &&
		capacity > eventGroup.capacity
	);
}

/**
 * Whether a typed capacity would strand someone already seated. minimumCapacity absent means
 * no event qualifies to be stranded, so nothing can be below it.
 */
export function isCapacityBelowMinimum(
	capacity: number | null,
	minimumCapacity: number | undefined
): boolean {
	return (
		capacity !== null &&
		minimumCapacity !== undefined &&
		capacity < minimumCapacity
	);
}

/** Waitlist figures with a capped upcoming session to split: every figure is set. */
export type SeatSplitFigures = {
	[Key in keyof WaitlistFigures]: NonNullable<WaitlistFigures[Key]>;
};

/**
 * Whether the overview splits the program's seats into booked, held and open: while it has a
 * waitlist and a capped upcoming session. The API titles the first overview card Booked on the
 * same condition, and Registered otherwise.
 */
export function showsSeatSplit(
	figures?: WaitlistFigures
): figures is SeatSplitFigures {
	return (
		!!figures &&
		figures.booked !== null &&
		figures.free !== null &&
		figures.held !== null &&
		figures.open !== null
	);
}
